<template>
  <Filters
    :groupedPropertyData="groupedPropertyData"
    :active="filteredType"
    @onBadgeClick="handleFilterClick"
  />
  <ValuationModelUpdateBanner :class="$style.banner" :isHidden="!hasPropertyPredatingModelSwitch" />
  <template v-for="groupedData in groupedPropertyData" :key="groupedData.type">
    <div
      v-if="
        groupedData.items.length && (filteredType === 'all' || groupedData.type === filteredType)
      "
      :class="$style.propertySection"
    >
      <h3 :class="$style.subtitle">
        {{ t(`overview.subtitle.${OfferType[groupedData.type].toLowerCase()}`) }}
      </h3>
      <Summary v-if="groupedData.type === OfferType.Own" :propertiesData="propertiesData" />
      <div :class="$style.propertyGroup">
        <PropertyTile
          v-for="item in groupedData.isFold
            ? groupedData.items.slice(0, propertyGroupSize)
            : groupedData.items"
          :key="item.id"
          :data="item"
          @onPropertyClick="handlePropertyClick"
          @onEdit="handleEdit"
          @onRemove="handleRemove"
        />
      </div>
      <div
        v-if="groupedData.isFold"
        :class="$style.showAllButton"
        data-testid="show-all-button"
        @click="
          () => {
            groupedData.isFold = false;
            trackShowAll(groupedData);
          }
        "
      >
        {{ t('common.showAll') }}
        <HgIcon icon="ChevronDown" />
      </div>
    </div>
  </template>
  <HgModal v-model="showConfirmDialog">
    <template #title>{{ $t('overview.removePropertyModal.title') }}</template>
    <p>{{ $t('overview.removePropertyModal.description') }}</p>
    <template #footer>
      <HgButton
        :disabled="isDeletingProperty"
        theme="secondary-bordered"
        @click="showConfirmDialog = false"
        >{{ $t('button.cancel') }}
      </HgButton>
      <HgButton
        name="confirm-remove-button"
        :disabled="isDeletingProperty"
        @click="confirmRemove"
        >{{ $t('overview.removePropertyModal.cta') }}</HgButton
      >
    </template>
  </HgModal>
</template>

<script setup lang="ts">
import { deletePropertyById } from '@/api/propertyValuation';
import ValuationModelUpdateBanner, {
  VALUATION_MODEL_SWITCH_TIMESTAMP,
} from '@/components/common/ValuationModelUpdateBanner/ValuationModelUpdateBanner.vue';
import Filters, { FilterType } from '@/components/property-overview-page/MyProperties/Filters.vue';
import PropertyTile from '@/components/property-overview-page/MyProperties/PropertyTile.vue';
import Summary from '@/components/property-overview-page/MyProperties/Summary.vue';
import {
  GroupedPropertyData,
  PropertyTileData,
  adaptData,
} from '@/components/property-overview-page/MyProperties/adaptData';
import { useMedia } from '@/composables/useMedia';
import { getPropertyDetailsPagePath } from '@/helpers/Url/Url';
import { PropertyValuationResult } from '@/models/queries/getPropertyData';
import { OfferType } from '@/models/valuationFunnel';
import { trackingClickEvent } from '@/tracking/tracking';
import { TRACKING_ACTION, TRACKING_LABEL, TRACKING_SHOW_ALL_LABEL } from '@/tracking/types';
import { ToastType } from '@/types';
import { HgButton, HgIcon, HgModal, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';

export interface Props {
  propertiesData: PropertyValuationResult[];
}

const emit =
  defineEmits<
    (event: 'onRemoveProperty' | 'onLastPropertyRemoved', offerType: OfferType) => void
  >();

const props = defineProps<Props>();

const { locale, t } = useI18n();
const { commit } = useStore();
const { push } = useRouter();
const { isLargeScreen, isSmallScreen } = useMedia();

const hasPropertyPredatingModelSwitch = computed<boolean>(() =>
  props.propertiesData.some(
    ({ createdDate }) => Date.parse(createdDate) < VALUATION_MODEL_SWITCH_TIMESTAMP,
  ),
);

const propertyGroupSize = computed<number>(() => {
  // default: shows 3 tiles, on tablet: shows 2 tiles
  return isSmallScreen.value === false && isLargeScreen.value === false ? 2 : 3;
});

const groupedPropertyData = ref<GroupedPropertyData[]>(
  adaptData(props.propertiesData, propertyGroupSize.value, locale.value as HgSupportedLanguage),
);
const filteredType = ref<FilterType>('all');
const showConfirmDialog = ref(false);
const propertyToRemove = ref<PropertyTileData | null>(null);
const isDeletingProperty = ref<boolean>(false);

watch(propertyGroupSize, (value) => {
  groupedPropertyData.value = adaptData(
    props.propertiesData,
    value,
    locale.value as HgSupportedLanguage,
  );
});

const trackShowAll = (targetGroup: GroupedPropertyData) => {
  trackingClickEvent(
    TRACKING_ACTION.SHOW_ALL_PROPERTIES,
    TRACKING_SHOW_ALL_LABEL[targetGroup.type],
    false,
  );
};

const handlePropertyClick = (id: string) => {
  // Probably has to be rewritten to use the HOW PropertyId
  const selectedProperty = props.propertiesData.find((item) => item.leadId === id);
  if (selectedProperty) {
    trackingClickEvent(
      TRACKING_ACTION.VIEW_PROPERTY_DASHBOARD,
      TRACKING_LABEL.VALUATION_FUNNEL,
      true,
      {
        h_lead_id_066: selectedProperty.leadId,
      },
    );
    // Capture hasNotEngaged from the list data BEFORE navigation triggers the detail GET,
    // which toggles the flag to false on the backend. This is the only reliable pre-toggle source.
    if (selectedProperty.hasNotEngaged) {
      commit('propertyDetails/setWasNotEngaged', true);
    }
    // Directly fetches the data for that property instead of using the one from the MyProperties store
    const { leadId } = selectedProperty;
    commit('propertyDetails/fetchPropertyData', { leadId, retries: 4 });
    push(getPropertyDetailsPagePath(locale.value as HgSupportedLanguage, id));
  }
};

const handleEdit = (id: string) => {
  const selectedProperty = props.propertiesData.find((item) => item.leadId === id);
  if (selectedProperty) {
    push(`${getPropertyDetailsPagePath(locale.value as HgSupportedLanguage, id)}/edit`);
  }
};

const handleFilterClick = (type: FilterType) => {
  filteredType.value = type;
};

const handleRemove = (property: PropertyTileData) => {
  propertyToRemove.value = property;
  showConfirmDialog.value = true;
};

const confirmRemove = async () => {
  if (propertyToRemove.value) {
    isDeletingProperty.value = true;
    const isDeletedSuccessfully = await deletePropertyById(propertyToRemove.value.id);

    if (isDeletedSuccessfully) {
      trackingClickEvent(
        TRACKING_ACTION.DELETE_PROPERTY,
        TRACKING_LABEL.PROPERTY_VALUATION_FORM,
        true,
        {
          h_lead_id_066: propertyToRemove.value.id,
        },
      );

      groupedPropertyData.value = groupedPropertyData.value.map((group) => {
        if (
          group.type === propertyToRemove.value!.offerType ||
          (group.type === OfferType.Own && propertyToRemove.value!.offerType === OfferType.Selling)
        ) {
          return {
            ...group,
            items: group.items.filter((item) => item.id !== propertyToRemove.value!.id),
          };
        }
        return group;
      });

      const hasProperties = groupedPropertyData.value.some((group) => group.items.length > 0);

      // eslint-disable-next-line max-depth
      if (!hasProperties) emit('onLastPropertyRemoved', propertyToRemove.value.offerType);

      emit('onRemoveProperty', propertyToRemove.value.offerType);
      commit('propertyDetails/setToastType', ToastType.RemoveSuccess);
    } else {
      commit('propertyDetails/setToastType', ToastType.RemoveFailure);
    }

    isDeletingProperty.value = false;
    showConfirmDialog.value = false;
    propertyToRemove.value = null;
  }
};
</script>

<style module lang="scss">
@use '@/assets/variables' as *;

.banner {
  // Filters' own margin-bottom ($space-150) always sits between it and whatever
  // follows, so offset it to land on the exact 16px top / 32px bottom the design
  // specifies; the property sections below have no margin-top to interfere with.
  margin-top: calc(#{$space-100} - #{$space-150});
  margin-bottom: $space-200;
}

.propertySection {
  margin-bottom: $space-200;
}

.subtitle {
  @include heading-3;
  @include vertical-margin;
}

.propertyGroup {
  display: grid;
  grid-template-columns: 1fr;
  gap: calc($space-150 - $space-25);
  white-space: nowrap;
  margin-top: $space-200;

  @include grid-media-query(sm) {
    grid-template-columns: repeat(2, 1fr);
  }

  @include grid-media-query(md) {
    grid-template-columns: repeat(3, 1fr);
  }
}

.showAllButton {
  display: inline-flex;
  align-items: center;
  gap: $space-50;
  cursor: pointer;
  margin-top: $space-100;
  @include body-3($color-black);

  > svg {
    width: $space-75;
    @include color-icon($color-black);
  }
}
</style>
